/*
 * Render the buttons that are displayed below a scratchpad.
 */
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {ModalLauncher} from "@khanacademy/wonder-blocks-modal";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {useGql, type GqlFetchFn} from "@khan/data-gql";

import {ClearFlagsControls} from "#clarifications-package/clear-flags-controls.tsx";
import FlagControls from "#clarifications-package/flag-controls.tsx";
import VoteControls from "#clarifications-package/vote-controls.tsx";
import withBackboneModels from "#react-components-package/with-backbone-models.tsx";
import type {Scratchpad} from "#scratchpad-base-package/scratchpads.ts";
import type ProfileModel from "#shared-package/profile-model.ts";

import DeleteButton from "./scratchpad-delete-button.tsx";
import SettingsButton from "./scratchpad-settings-button.tsx";
import ShareModal from "./share-modal.tsx";
import type {DiscussionMeta} from "./types.ts";

type Props = {
    // Does the current user have permission to edit this scratchpad?
    canEdit: boolean;
    // The profile object of the person who made this program
    // @ts-expect-error - TS2749 - 'ProfileModel' refers to a value, but is being used as a type here. Did you mean 'typeof ProfileModel'?
    creatorProfile: ProfileModel | null | undefined;
    // Settings to pass in to the discussion meta control
    discussionMeta?: DiscussionMeta;
    // Is this scratchpad embedded in another page (either via an iframe
    // or as inside a tutorial)
    embedded: boolean;
    // If we are in the edit mode for a scratchpad (as a dev) and have
    // permission to edit this scratchpad
    inEditModeAndCanEdit?: boolean;
    // Is this a documentation scratchpad?
    isDocumentation: boolean;
    // Is the active user a moderator?
    isModerator: boolean;
    // Is this scratchpad new (e.g. on the /new page)
    isNew?: boolean;
    // Is this an official scratchpad?
    isOfficial: boolean;
    // Is the current user the owner of this scratchpad?
    isOwner: boolean;
    // Are spinoffs disabled?
    hideSpinOffs?: boolean;
    // Title used for the "New program" button
    newTitle: string;
    // Handle when properties are toggled (such as for the mod controls)
    onToggleProgramProp: (
        gqlFetch: GqlFetchFn,
        property: "deleted" | "hideFromHotlist",
    ) => Promise<void>;
    // Is the user loading this program a child?
    private: boolean;
    // The scratchpad Backbone model which is being rendered
    // @ts-expect-error - TS2749 - 'Scratchpad' refers to a value, but is being used as a type here. Did you mean 'typeof Scratchpad'?
    scratchpad: Scratchpad;
    // The discussion key used to manage the voting, flagging, comments
    // and moderation of this scratchpad.
    // NOTE(jeresig): This use to be called "key" but that name conflicts
    // with the "key" property that's used by React.
    scratchpadKey: string | null | undefined;
    // Should the share button be shown? Defaults to true.
    showShareButton?: boolean;
    // Callback to call when saving changes
    onSave: (saveSettings: boolean) => unknown;
    // Callback to determine if the scratchpaad has unsaved changes
    hasUnsavedChanges: () => boolean;
    // DeleteScratchpadButton calls this function to delete a scratchpad
    deleteProgram: (gqlFetch: GqlFetchFn) => Promise<void>;
};

class ScratchpadButtons extends React.Component<Props> {
    _mounted: boolean;

    static defaultProps: {
        showShareButton: boolean;
    } = {
        showShareButton: true,
    };

    constructor(props: Props) {
        super(props);

        this._mounted = false;
    }

    componentDidMount() {
        this._mounted = true;
    }

    componentWillUnmount() {
        this._mounted = false;
    }

    isOfficial(): boolean {
        return (
            this.props.embedded ||
            this.props.isOfficial ||
            this.props.isDocumentation
        );
    }

    // Hard delete button
    renderDeleteButton(): React.ReactNode {
        if (
            this.props.hideSpinOffs ||
            this.props.isNew ||
            !this.props.isOwner
        ) {
            return null;
        }

        // TODO: intermittently returns false for "!this.props.isOwner"
        //       figure out why that is--it was resulting in the Delete button
        //       sometimes showing in incorrect contexts
        return <DeleteButton deleteProgram={this.props.deleteProgram} />;
    }

    // Settings button
    renderSettingsButton(): React.ReactNode {
        if (this.props.isNew || !this.props.inEditModeAndCanEdit) {
            return null;
        }

        const {onSave, scratchpad} = this.props;

        return <SettingsButton onSave={onSave} scratchpad={scratchpad} />;
    }

    renderNewProjectButton(): React.ReactNode {
        const newUrlPath = this.props.scratchpad.get("newUrlPath");

        if (this.props.isNew || this.props.embedded || !newUrlPath) {
            return null;
        }

        return (
            <Button kind="secondary" href={newUrlPath} target="_blank">
                {this.props.newTitle}
            </Button>
        );
    }

    renderChildAccountNotice(): React.ReactNode {
        // private will be set for child accounts no matter who is loading the
        // page. creatorProfile will be set only for users who are allowed to
        // see the message.
        if (
            this.props.isNew ||
            !this.props.private ||
            !this.props.creatorProfile
        ) {
            return null;
        }

        return (
            <Button kind="secondary" disabled={true}>
                {t`Child account program`}
            </Button>
        );
    }

    renderFlagControls(): React.ReactNode {
        const {isNew, scratchpadKey, discussionMeta} = this.props;
        if (
            isNew ||
            this.props.private ||
            this.isOfficial() ||
            !scratchpadKey
        ) {
            return null;
        }

        // Always show one and only one of these depending on the user
        let FlagButton;
        if (this.props.isModerator) {
            const clearFlagsProps = {
                flaggedBy: discussionMeta?.flaggedBy,
                scratchpadKey: scratchpadKey,
                flags: discussionMeta?.flags || [],
                inQueue: discussionMeta?.inQueue || false,
                definitelyNotSpam: discussionMeta?.definitelyNotSpam || false,
            } as const;

            FlagButton = <ClearFlagsControls {...clearFlagsProps} />;
        } else if (!discussionMeta?.hideFlagControls) {
            const flagProps = {
                id: scratchpadKey,
                flaggedByUser: discussionMeta?.flaggedByUser || false,
                flagButton: discussionMeta?.flagButton || false,
            } as const;

            FlagButton = <FlagControls {...flagProps} />;
        }

        return FlagButton;
    }

    // Voting Controls
    renderVotingControls(): React.ReactNode {
        if (
            this.props.isNew ||
            this.props.private ||
            this.isOfficial() ||
            !this.props.scratchpadKey
        ) {
            return null;
        }

        const hideVoteControls = !this.props.discussionMeta?.voteUpOnly;

        if (!this.props.discussionMeta || hideVoteControls) {
            return null;
        }

        const voteProps = {
            scratchpadKey: this.props.scratchpadKey,
            sumVotesIncremented: this.props.discussionMeta.sumVotesIncremented,
            upVoted: this.props.discussionMeta.upVoted || false,
        } as const;

        return <VoteControls {...voteProps} />;
    }

    // Share Button
    renderShareButton(): React.ReactNode {
        if (
            this.props.isNew ||
            this.props.private ||
            !this.props.showShareButton
        ) {
            return null;
        }

        const {hasUnsavedChanges, scratchpad} = this.props;
        const shareModal = ({closeModal}: {closeModal: () => void}) => (
            <ShareModal
                hasUnsavedChanges={hasUnsavedChanges}
                scratchpad={scratchpad}
            />
        );

        return (
            <ModalLauncher modal={shareModal}>
                {({openModal}) => (
                    <Button kind="secondary" onClick={openModal}>
                        {t`Share`}
                    </Button>
                )}
            </ModalLauncher>
        );
    }

    render(): React.ReactNode {
        const buttons = [
            {key: "delete", rendered: this.renderDeleteButton()},
            {key: "settings", rendered: this.renderSettingsButton()},
            {key: "child", rendered: this.renderChildAccountNotice()},
            {key: "voting", rendered: this.renderVotingControls()},
            {key: "flag", rendered: this.renderFlagControls()},
            {key: "share", rendered: this.renderShareButton()},
            {key: "new", rendered: this.renderNewProjectButton()},
        ]
            .filter((button) => {
                return button.rendered != null;
            })
            .map((button, ind) => {
                return (
                    <React.Fragment key={button.key}>
                        {button.rendered}
                        <Strut size={spacing.small_12} />
                    </React.Fragment>
                );
            });

        const {
            isNew,
            isModerator,
            scratchpad,
            onToggleProgramProp,
            private: isPrivate,
        } = this.props;
        return (
            <React.Fragment>
                <View style={styles.buttonsRow}>{buttons}</View>
                <ModeratorsArea
                    {...{
                        isNew,
                        isPrivate,
                        isModerator,
                        scratchpad,
                        onToggleProgramProp,
                    }}
                />
            </React.Fragment>
        );
    }
}

// Moderators area, for non official programs.
function ModeratorsArea({
    isNew,
    isPrivate,
    isModerator,
    scratchpad,
    onToggleProgramProp,
}: {
    isNew?: boolean;
    isPrivate: boolean;
    isModerator: boolean;
    // @ts-expect-error - TS2749 - 'Scratchpad' refers to a value, but is being used as a type here. Did you mean 'typeof Scratchpad'?
    scratchpad: Scratchpad;
    onToggleProgramProp: (
        gqlFetch: GqlFetchFn,
        property: "deleted" | "hideFromHotlist",
    ) => Promise<void>;
}): React.ReactElement | null {
    const gqlFetch = useGql();

    if (
        isNew ||
        scratchpad.get("isPublished") ||
        isPrivate ||
        !isModerator ||
        // Since new content kinds (Talkthroughs, Challenges) are
        // published content kinds and cannot be user generated,
        // we only need to hide/report the old Scratchpad content kind.
        scratchpad.get("contentKind") !== "Scratchpad"
    ) {
        return null;
    }

    const softDeleteText = scratchpad.get("deleted")
        ? t`Undo hide everywhere`
        : t`Hide everywhere`;
    const hideHotlistText = scratchpad.get("hideFromHotlist")
        ? t`Undo hide from hotlist`
        : t`Hide from hotlist`;

    return (
        <View style={styles.buttonsRow}>
            {/* Soft delete Button */}
            <Button
                kind="secondary"
                onClick={() => onToggleProgramProp(gqlFetch, "deleted")}
            >
                {softDeleteText}
            </Button>
            <Strut size={spacing.small_12} />
            {/* Hide from hotlist */}
            <Button
                kind="secondary"
                onClick={() => onToggleProgramProp(gqlFetch, "hideFromHotlist")}
            >
                {hideHotlistText}
            </Button>
        </View>
    );
}

const styles = StyleSheet.create({
    buttonsRow: {
        flexDirection: "row",
        justifyContent: "center",
        marginBottom: 22,
    },
});

export default withBackboneModels(({scratchpad}: Props) => [scratchpad])(
    ScratchpadButtons,
) as React.ComponentType<
    JSX.LibraryManagedAttributes<
        typeof ScratchpadButtons,
        React.ComponentProps<typeof ScratchpadButtons>
    >
>;
